import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { ROUTE_CLIENT_ORGANIZATIONS_VIEW } from '../../../config/routes';
import { formatMinorUnits, formatPercent, formatQuantity } from '../reportFormatters';
import { CommercialValue } from './CommercialValue';

const SORTABLE_COLUMNS = {
  name: 'components.pages.reports.organizations.organization',
  fulfilled_quantity: 'components.pages.reports.organizations.quantity',
  orders: 'components.pages.reports.organizations.orders',
  net_sales_without_vat: 'components.pages.reports.organizations.netWithoutVat',
  net_sales_with_vat: 'components.pages.reports.organizations.netWithVat'
};

function OrganizationSortButton({ column, table, onChange }) {
  const isActive = table.sortBy === column;
  const nextDirection = isActive && table.sortDirection === 'desc' ? 'asc' : 'desc';

  return (
    <button
      className="report-organizations__sort"
      onClick={() => onChange({ ...table, page: 1, sortBy: column, sortDirection: nextDirection })}
      type="button"
    >
      <FormattedMessage id={SORTABLE_COLUMNS[column]}/>
      <i
        aria-hidden="true"
        className={`fas ${isActive && table.sortDirection === 'asc' ? 'fa-sort-up' : isActive ? 'fa-sort-down' : 'fa-sort'}`}
      />
    </button>
  );
}

function OrganizationIdentity({ row }) {
  const name = row.name || '—';

  if (!row.uuid) {
    return <strong className="report-organizations__name">{name}</strong>;
  }

  return (
    <strong className="report-organizations__name">
      <Link to={ROUTE_CLIENT_ORGANIZATIONS_VIEW.replace(':organizationId', row.uuid)}>{name}</Link>
    </strong>
  );
}

function OrganizationRow({ row, labels }) {
  const quantity = row.fulfilled_quantity || {};
  const orders = row.orders || {};
  const comparison = row.comparison && row.comparison.changes;
  const directAllocationOnly = Number(orders.billable || 0) === 0
    && Number(orders.direct_allocation || 0) > 0;

  return (
    <tr data-testid={`organization-report-row-${row.uuid || row.id}`}>
      <td data-label={labels.organization}>
        <OrganizationIdentity row={row}/>
        {directAllocationOnly && (
          <span className="report-organizations__allocation-badge">
            <FormattedMessage id="components.pages.reports.organizations.directAllocationOnly"/>
          </span>
        )}
      </td>
      <td data-label={labels.quantity}>
        <strong>{formatQuantity(quantity.net)}</strong>
        <span className="report-organizations__details">
          <FormattedMessage id="components.pages.reports.organizations.purchased"/>: {formatQuantity(quantity.purchased)}
          {' · '}<FormattedMessage id="components.pages.reports.organizations.direct"/>: {formatQuantity(quantity.direct_allocated)}
          {' · '}<FormattedMessage id="components.pages.reports.organizations.refunded"/>: {formatQuantity(quantity.refunded)}
        </span>
      </td>
      <td data-label={labels.orders}>
        <strong>{formatQuantity(orders.total)}</strong>
        <span className="report-organizations__details">
          <FormattedMessage id="components.pages.reports.organizations.billable"/>: {formatQuantity(orders.billable)}
          {' · '}<FormattedMessage id="components.pages.reports.organizations.direct"/>: {formatQuantity(orders.direct_allocation)}
        </span>
      </td>
      <td data-label={labels.netWithoutVat}>
        <CommercialValue basis="without_vat" commercialValue={row.commercial_value} directAllocationValue={row.direct_allocation_value} netSales={row.net_sales}/>
        {comparison && (
          <span className="report-organizations__comparison">
            {formatPercent(comparison.net_sales_without_vat_percent)}
          </span>
        )}
      </td>
      <td data-label={labels.netWithVat}>
        <CommercialValue basis="with_vat" commercialValue={row.commercial_value} directAllocationValue={row.direct_allocation_value} netSales={row.net_sales}/>
      </td>
      <td data-label={labels.vatRefunds}>
        <strong>{formatMinorUnits(row.net_sales, 'vat')}</strong>
        <span className="report-organizations__details">
          <FormattedMessage id="components.pages.reports.organizations.refunds"/>: {formatMinorUnits(row.refunds, 'with_vat')}
        </span>
        <span className="report-organizations__details">
          <FormattedMessage id="components.pages.reports.organizations.refundRate"/>: {formatPercent(row.refund_rate)}
        </span>
      </td>
    </tr>
  );
}

function OrganizationsTable({ data, table, onTableChange }) {
  const intl = useIntl();
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const pagination = (data.meta && data.meta.pagination) || {};
  const currentPage = Number(pagination.page || table.page);
  const lastPage = Math.max(1, Number(pagination.last_page || 1));
  const total = Number(pagination.total || 0);
  const perPage = Number(pagination.per_page || table.perPage);
  const firstResult = total === 0 ? 0 : ((currentPage - 1) * perPage) + 1;
  const lastResult = Math.min(total, currentPage * perPage);
  const labels = {
    organization: intl.formatMessage({ id: 'components.pages.reports.organizations.organization' }),
    quantity: intl.formatMessage({ id: 'components.pages.reports.organizations.quantity' }),
    orders: intl.formatMessage({ id: 'components.pages.reports.organizations.orders' }),
    netWithoutVat: intl.formatMessage({ id: 'components.pages.reports.organizations.netWithoutVat' }),
    netWithVat: intl.formatMessage({ id: 'components.pages.reports.organizations.netWithVat' }),
    vatRefunds: intl.formatMessage({ id: 'components.pages.reports.organizations.vatRefunds' })
  };
  const pageTo = page => onTableChange({ ...table, page });

  return (
    <section
      aria-label={intl.formatMessage({ id: 'components.pages.reports.organizations.tableLabel' })}
      className="report-organizations"
    >
      <div className="report-organizations__table-wrap">
        <table className="report-organizations__table">
          <colgroup>
            <col className="report-organizations__col-organization"/>
            <col className="report-organizations__col-quantity"/>
            <col className="report-organizations__col-orders"/>
            <col className="report-organizations__col-money"/>
            <col className="report-organizations__col-money"/>
            <col className="report-organizations__col-vat"/>
          </colgroup>
          <thead>
            <tr>
              {['name', 'fulfilled_quantity', 'orders'].map(column => (
                <th aria-sort={table.sortBy === column ? `${table.sortDirection}ending` : 'none'} key={column} scope="col">
                  <OrganizationSortButton column={column} onChange={onTableChange} table={table}/>
                </th>
              ))}
              {['net_sales_without_vat', 'net_sales_with_vat'].map(column => (
                <th aria-sort={table.sortBy === column ? `${table.sortDirection}ending` : 'none'} key={column} scope="col">
                  <OrganizationSortButton column={column} onChange={onTableChange} table={table}/>
                </th>
              ))}
              <th scope="col">{labels.vatRefunds}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => <OrganizationRow key={row.uuid || row.id} labels={labels} row={row}/>)}
          </tbody>
        </table>
      </div>

      <footer className="report-organizations__footer">
        <label className="report-organizations__rows">
          <FormattedMessage id="components.pages.reports.organizations.rowsPerPage"/>
          <select
            aria-label={intl.formatMessage({ id: 'components.pages.reports.organizations.rowsPerPage' })}
            onChange={event => onTableChange({ ...table, page: 1, perPage: Number(event.target.value) })}
            value={table.perPage}
          >
            {[10, 25, 50, 100].map(value => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <span aria-live="polite" className="report-organizations__range">
          <FormattedMessage
            id="components.pages.reports.organizations.range"
            values={{ first: firstResult, last: lastResult, total }}
          />
        </span>
        <nav
          aria-label={intl.formatMessage({ id: 'components.pages.reports.organizations.pagination' })}
          className="report-organizations__pagination"
        >
          <button aria-label={intl.formatMessage({ id: 'components.pages.reports.organizations.firstPage' })} disabled={currentPage <= 1} onClick={() => pageTo(1)} type="button">
            <i aria-hidden="true" className="fas fa-angle-double-left"/>
          </button>
          <button aria-label={intl.formatMessage({ id: 'components.pages.reports.organizations.previousPage' })} disabled={currentPage <= 1} onClick={() => pageTo(currentPage - 1)} type="button">
            <i aria-hidden="true" className="fas fa-angle-left"/>
          </button>
          <span>{currentPage} / {lastPage}</span>
          <button aria-label={intl.formatMessage({ id: 'components.pages.reports.organizations.nextPage' })} disabled={currentPage >= lastPage} onClick={() => pageTo(currentPage + 1)} type="button">
            <i aria-hidden="true" className="fas fa-angle-right"/>
          </button>
          <button aria-label={intl.formatMessage({ id: 'components.pages.reports.organizations.lastPage' })} disabled={currentPage >= lastPage} onClick={() => pageTo(lastPage)} type="button">
            <i aria-hidden="true" className="fas fa-angle-double-right"/>
          </button>
        </nav>
      </footer>

      {data.meta && (
        <div className="report-organizations__freshness">
          {data.meta.indexed_at && (
            <span><FormattedMessage id="components.pages.reports.summary.updated"/>: {data.meta.indexed_at}</span>
          )}
          {Number(data.meta.fallback_payment_timestamp_count || 0) > 0 && (
            <span className="report-summary__freshness-warning">
              <FormattedMessage
                id="components.pages.reports.summary.fallbackTimestamps"
                values={{ count: data.meta.fallback_payment_timestamp_count }}
              />
            </span>
          )}
        </div>
      )}
    </section>
  );
}

function OrganizationsReport({ data, error, loading, onRetry, onTableChange, table }) {
  if (loading) {
    return (
      <div aria-live="polite" className="report-organizations-loading" data-testid="organizations-loading" role="status">
        <span/><span/><span/>
        <span className="sr-only"><FormattedMessage id="components.pages.reports.organizations.loading"/></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-state report-state--error" role="alert">
        <FormattedMessage id="components.pages.reports.organizations.error"/>
        <button className="btn btn-outline-primary" onClick={onRetry} type="button">
          <FormattedMessage id="components.pages.reports.organizations.retry"/>
        </button>
      </div>
    );
  }

  if (!data || !Array.isArray(data.rows) || data.rows.length === 0) {
    return <div className="report-state"><FormattedMessage id="components.pages.reports.organizations.empty"/></div>;
  }

  return <OrganizationsTable data={data} onTableChange={onTableChange} table={table}/>;
}

export { OrganizationRow, OrganizationsReport, OrganizationsTable };
